import { useOrganization } from './useOrganization';
import { hasRole, canInvite, canModifyMember, type OrgRole } from '@/types/organization';

interface UsePermissionsReturn {
  role: OrgRole | null;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isViewer: boolean;
  canEditOrg: boolean;
  canDeleteOrg: boolean;
  canInviteMembers: boolean;
  canManageMembers: boolean;
  canManageSettings: boolean;
  canLeave: boolean;
  hasRole: (requiredRole: OrgRole) => boolean;
  canModifyMember: (targetRole: OrgRole) => boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { currentOrg, currentRole, currentOrgWithMembership } = useOrganization();

  const allowMemberInvites =
    currentOrg?.settings.allowMemberInvites ??
    currentOrgWithMembership?.settings.allowMemberInvites ??
    false;

  const role = currentRole;
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || isOwner;
  const isMember = role === 'member' || isAdmin;
  const isViewer = role === 'viewer';

  return {
    role,
    isOwner,
    isAdmin,
    isMember,
    isViewer,
    canEditOrg: isAdmin,
    canDeleteOrg: isOwner,
    canInviteMembers: role ? canInvite(role, allowMemberInvites) : false,
    canManageMembers: isAdmin,
    canManageSettings: isAdmin,
    canLeave: (isMember || isViewer) && !isOwner,
    hasRole: (requiredRole: OrgRole) => (role ? hasRole(role, requiredRole) : false),
    canModifyMember: (targetRole: OrgRole) => (role ? canModifyMember(role, targetRole) : false),
  };
}

export default usePermissions;
